/**
 * Helpers for emitting per-page JSON-LD (Article, BreadcrumbList,
 * FinancialProduct, WebPage). Keeps schema consistent across routes.
 *
 * Every URL emitted here is normalized to the canonical production origin
 * (https://dscrloanmarket.com) so Google Rich Results and
 * validators do not reject relative or off-domain identifiers.
 */
import samLogo from "@/assets/simply-approved-logo-horizontal.png.asset.json";
import { SITE, LENDER } from "./lender";

/** Canonical origin used for every schema `@id` / `url` / `item`. */
export const CANONICAL_ORIGIN = "https://dscrloanmarket.com";

/**
 * Turn a project-relative path ("/hecm") into an absolute canonical URL.
 * Always returns a syntactically valid absolute https URL: whitespace and
 * other unsafe characters are percent-encoded, and anything unparseable
 * falls back to the canonical origin. Google rejects breadcrumb/schema
 * items whose URL is relative, empty, or contains raw spaces.
 */
export function absoluteUrl(path: string): string {
  const raw = typeof path === "string" ? path.trim() : "";
  if (!raw) return CANONICAL_ORIGIN + "/";
  const candidate = /^https?:\/\//i.test(raw)
    ? raw
    : CANONICAL_ORIGIN + (raw.startsWith("/") ? raw : "/" + raw);
  try {
    return new URL(encodeURI(decodeURI(candidate))).toString();
  } catch {
    return CANONICAL_ORIGIN + "/";
  }
}


const ORG = {
  "@type": "MortgageBroker",
  "@id": `${CANONICAL_ORIGIN}/#organization`,
  name: LENDER.name,
  identifier: LENDER.nmlsNumber,
  url: CANONICAL_ORIGIN,
  logo: samLogo.url,
  areaServed: [
    { "@type": "State", name: "Florida" },
    { "@type": "State", name: "Colorado" },
  ],
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
  };
}

type Lang = "en-US" | "es-US";

export interface ArticleSchemaInput {
  path: string;
  title: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  inLanguage?: Lang;
}

export function articleSchema({
  path,
  title,
  description,
  datePublished,
  dateModified,
  inLanguage = "en-US",
}: ArticleSchemaInput) {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    inLanguage,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: SITE.legalName, url: CANONICAL_ORIGIN },
    publisher: ORG,
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  const clean = items.filter((it) => it && typeof it.name === "string" && it.name.trim().length > 0);
  const last = clean.length - 1;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: clean.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name.trim(),
      // Google's guidance: the final crumb is the current page and should
      // omit `item`. Earlier crumbs use an explicit absolute @id URL.
      ...(i === last && clean.length > 1
        ? {}
        : { item: { "@type": "WebPage", "@id": absoluteUrl(it.path), url: absoluteUrl(it.path), name: it.name.trim() } }),
    })),
  };
}


export function faqSchema(items: { q: string; a: string }[]) {
  // NOTE: `speakable` was removed. Speakable requires CSS selectors that
  // resolve to the visible answer text on the rendered page. Our FAQ
  // components do not annotate answers with `[data-speakable]`, so keeping
  // the property was inaccurate/misleading schema.
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function financialProductSchema({
  name,
  description,
  path,
}: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    name,
    description,
    category: "Reverse Mortgage",
    provider: ORG,
    url: absoluteUrl(path),
    areaServed: [
      { "@type": "State", name: "Florida" },
      { "@type": "State", name: "Colorado" },
    ],
  };
}

export function webPageSchema({
  path,
  title,
  description,
  inLanguage = "en-US",
}: {
  path: string;
  title: string;
  description: string;
  inLanguage?: Lang;
}) {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    url,
    name: title,
    description,
    inLanguage,
    isPartOf: {
      "@type": "WebSite",
      "@id": `${CANONICAL_ORIGIN}/#website`,
      name: SITE.name,
      url: CANONICAL_ORIGIN,
    },
  };
}

/**
 * LocalBusiness (MortgageBroker) JSON-LD scoped to a specific city — used on
 * state/city landing pages to compete in local pack and voice search
 * ("DSCR loan broker near me"). Built from real Simply Approved Mortgages
 * Mortgages brokerage details; areaServed names the actual city + county.
 */
export function localBusinessSchema({
  path,
  cityName,
  stateName,
  stateAbbr,
  county,
}: {
  path: string;
  cityName: string;
  stateName: string;
  stateAbbr: string;
  county: string;
}) {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "MortgageBroker",
    "@id": `${url}#localbusiness`,
    name: `${LENDER.name} — Reverse mortgage loan officer serving ${cityName}, ${stateAbbr}`,
    description: `Mortgage broker arranging business-purpose investment property financing for real estate investors in ${cityName} and surrounding ${county} County, ${stateName}.`,
    url,
    logo: samLogo.url,
    identifier: LENDER.nmlsNumber,
    areaServed: [
      { "@type": "City", name: cityName, containedInPlace: { "@type": "AdministrativeArea", name: `${county} County, ${stateName}` } },
      { "@type": "AdministrativeArea", name: `${county} County, ${stateName}` },
      { "@type": "State", name: stateName },
    ],
    serviceType: ["DSCR rental property loan", "DSCR cash-out refinance", "Ground-up construction loan", "Multifamily and commercial financing", "Section 8 rental financing"],
    audience: { "@type": "Audience", audienceType: "Real estate investors and landlords" },
  };
}

/**
 * HowTo JSON-LD — for procedural pages (closing process, how it works).
 * Powers voice-assistant step-by-step answers and "How to" rich results.
 */
export function howToSchema({
  name,
  description,
  totalTime,
  steps,
}: {
  name: string;
  description: string;
  /** ISO 8601 duration, e.g. "P45D" for 45 days. */
  totalTime?: string;
  steps: { name: string; text: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    ...(totalTime ? { totalTime } : {}),
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/** Convenience: build the scripts array for a guide route. */
export function guideSchemaScripts(
  input: ArticleSchemaInput & {
    breadcrumbs: { name: string; path: string }[];
    faqs?: { q: string; a: string }[];
    financialProduct?: { name: string; description: string };
    localBusiness?: { cityName: string; stateName: string; stateAbbr: string; county: string };
    howTo?: { name: string; description: string; totalTime?: string; steps: { name: string; text: string }[] };
  },
) {
  const scripts: { type: string; children: string }[] = [
    { type: "application/ld+json", children: JSON.stringify(articleSchema(input)) },
    {
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbSchema(input.breadcrumbs)),
    },
  ];
  // FAQPage schema is emitted by the visible <FAQBlock> component, which
  // every guide route renders. Emitting it here too would double-list
  // Q&As in Google Rich Results.
  void input.faqs;
  if (input.financialProduct) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(
        financialProductSchema({
          name: input.financialProduct.name,
          description: input.financialProduct.description,
          path: input.path,
        }),
      ),
    });
  }
  if (input.localBusiness) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(
        localBusinessSchema({ path: input.path, ...input.localBusiness }),
      ),
    });
  }
  if (input.howTo) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(howToSchema(input.howTo)),
    });
  }
  return scripts;
}
